import { AfterViewInit, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { GridModule, PagerModule } from '@syncfusion/ej2-angular-grids';
import { SplitterComponent, SplitterModule } from '@syncfusion/ej2-angular-layouts';

import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GlobalApplicationToolbarComponent } from './core/components/global-application-toolbar/global-application-toolbar.component';
import { ToastComponent } from './core/components/toast/toast.component';
import { AuthenticationService } from './core/services/authentication.service';
import { ToolbarButtonComponent } from './shared/components/toolbar-button/toolbar-button.component';
import { Store } from '@ngrx/store';
import { selectTitle } from './store/calculation/calculation.selectors';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IndexedDbService } from './services/indexeddb.service';
import { ZoomControlComponent } from './core/components/zoom/zoom-control/zoom-control.component';
import { RouteLogoSpinnerComponent } from './shared/components/route-logo-spinner/route-logo-spinner.component';
import { RoutingLoaderService } from './services/routing-loader.service';
import { AsyncPipe } from '@angular/common';
import { LoggingService } from './services/logging.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ZoomService } from './services/zoom.service';
import { BackgroundJobsIndicatorComponent } from './shared/components/background-jobs-indicator/background-jobs-indicator.component';
import { HasRoleDirective } from './shared/directives/has-role.directive';
import { HasPermissionDirective } from './shared/directives/has-permission.directive';
import { Dialog } from '@angular/cdk/dialog';
import {
  CreateCalculationDialogComponent,
  CreateCalculationDialogResult,
} from './features/calculations/create-calculation/create-calculation-dialog/create-calculation-dialog.component';
import { HelpService } from './services/help.service';
import { HelpPanelComponent } from './features/help/help-panel/help-panel.component';

@Component({
  selector: 'app-root',
  imports: [
    GridModule,
    PagerModule,
    SplitterModule,
    HelpPanelComponent,
    TranslateModule,
    GlobalApplicationToolbarComponent,
    RouterModule,
    ToastComponent,
    ToolbarButtonComponent,
    ZoomControlComponent,
    RouteLogoSpinnerComponent,
    AsyncPipe,
    NgbTooltip,
    BackgroundJobsIndicatorComponent,
    HasRoleDirective,
    HasPermissionDirective,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  host: {
    // Bound on the document so F1 works wherever focus happens to be. This component is mounted once,
    // so there is exactly one listener.
    '(document:keydown)': 'onKeydown($event)',
  },
})
export class AppComponent implements OnInit, AfterViewInit {
  private readonly _helpService = inject(HelpService);
  @ViewChild('helpSplitter') private _helpSplitter?: SplitterComponent;

  /** Index of the help pane inside the shell splitter. */
  private static readonly HELP_PANE_INDEX = 1;

  loginDisplay = false;
  title = 'Andritz PreCalc';

  private readonly _authService = inject(AuthenticationService);
  private readonly _indexedDbService = inject(IndexedDbService);
  private readonly _translateService = inject(TranslateService);
  private readonly _store = inject(Store);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _routingLoaderService = inject(RoutingLoaderService);
  private readonly _loggingService = inject(LoggingService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _zoomService = inject(ZoomService);
  private readonly _dialog = inject(Dialog);

  isNavigating$ = this._routingLoaderService.isLoading$;

  constructor() {
    this._translateService.addLangs(['en']);
    this._translateService.setDefaultLang('en');
    this._translateService.use('en');
  }

  ngAfterViewInit(): void {
    // The splitter only honours `collapsed` at initial render, so runtime changes go through its API.
    this._helpService.isOpen$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((open) => this._syncHelpPane(open));
  }

  private _syncHelpPane(open: boolean): void {
    const splitter = this._helpSplitter;

    if (!splitter || splitter.paneSettings[AppComponent.HELP_PANE_INDEX]?.collapsed === !open) {
      return;
    }

    if (open) {
      splitter.expand(AppComponent.HELP_PANE_INDEX);
    } else {
      splitter.collapse(AppComponent.HELP_PANE_INDEX);
    }
  }

  toggleHelp(): void {
    this._helpService.toggle();
  }

  /**
   * F1 toggles the help panel, the same as the toolbar button.
   * preventDefault stops the browser's own help from opening; the `repeat` guard stops a held key
   * from flapping the panel open and shut.
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'F1' || event.repeat) {
      return;
    }
    event.preventDefault();
    this.toggleHelp();
  }

  protected readonly open = open;
  demoModal: boolean = false;

  async ngOnInit(): Promise<void> {
    this._authService.initMsal();

    try {
      await this._indexedDbService.initialize();
    } catch (error) {
      this._loggingService.error(
        'IndexedDB Initialization Failed',
        new HttpErrorResponse({ error: error instanceof Error ? error : new Error(String(error)) })
      );
    }

    this._store
      .select(selectTitle)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((title) => (this.title = title || 'Andritz PreCalc'));

    this._route.queryParamMap.subscribe((params) => {
      const calcId = params.get('calcId');
      const mode = params.get('mode');
      if (calcId) {
        // Navigate to the calculations page
        this._router.navigate(['/calculations', calcId], {
          queryParams: { mode: mode || 'edit' },
        });
      }
    });

    const zoomLevel = localStorage.getItem('zoomLevel');
    if (zoomLevel) {
      this._zoomService.setZoomLevel(Number(zoomLevel));
    }
  }

  isAuthenticated(): boolean {
    return this._authService.isAuthenticated();
  }

  onZoomChange(zoomLevel: number) {
    this._zoomService.setZoomLevel(zoomLevel);
  }

  onNewCalculation(): void {
    const dialogRef = this._dialog.open<CreateCalculationDialogResult | null>(CreateCalculationDialogComponent, {
      disableClose: false,
      width: '600px',
      data: {
        title: 'Create a new calculation',
      },
      panelClass: 'adf-dialog',
    });

    dialogRef.closed.subscribe((result) => {
      if (!result) {
        return;
      }

      // TODO: call the create-calculation API once available and navigate to the new calculation
    });
  }
}
